import { useState } from "react";
import { getVisitorGeo } from "@barrykilby/geo";
import { ALLOW_AS_COUNTRIES } from "../root";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";
import { Button } from "@/components/ui/button";
import { Link, redirect } from "react-router";
import type { Route } from "./+types/_public._index";
import {
  Invoice,
  UsersThree,
  Lightning,
  ShieldCheck,
  CheckCircle,
  ArrowRight,
  House,
  CalendarBlank,
  Camera,
  Star,
  CaretDown,
} from "@phosphor-icons/react";
import { TRADES, type TradeRegion } from "@/lib/for-trades";
import { MANOS_SALES_EMAIL } from "../../config/branding";
import {
  PLANS,
  entryMonthlyDisplay,
  promoMonthlyDisplay,
} from "@/lib/plans";
import { taxCopyForCountry } from "@/lib/taxCopy";

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const userId = context.session?.get("user");
  if (userId) return redirect("/inbox");
  const geo = getVisitorGeo(request, { allowAsOverride: ALLOW_AS_COUNTRIES });
  // Bucket the visitor for the testimonials carousel. Aligns with the
  // canonical TradeRegion union exported from for-trades.ts so the
  // homepage and per-trade pages share one source of truth (#129/#133).
  const region: TradeRegion =
    geo.country === "US"
      ? "US"
      : geo.country === "CA"
        ? "CA"
        : geo.country === "GB"
          ? "GB"
          : geo.isEU
            ? "EU"
            : "ROW";
  return { region, country: geo.country };
};

type RegionTestimonial = { quote: string; name: string; role: string };

const TESTIMONIALS_BY_REGION: Record<TradeRegion, RegionTestimonial[]> = {
  GB: [
    {
      quote:
        "I used to spend Sunday evenings sorting invoices and chasing payments. Now it takes me ten minutes a week. The quick capture is the thing -- I just say what the job is and it fills everything in.",
      name: "Craig Halliwell",
      role: "Gas engineer, Manchester",
    },
    {
      quote:
        "Every sparky I know is drowning in WhatsApp messages and spreadsheets. Manos sorted all of that in about an hour. My clients get proper invoices, I get paid faster. Can't ask for more.",
      name: "Priya Bhatia",
      role: "Electrician, Bristol",
    },
    {
      quote:
        "I run a three-person outfit and the shared client list alone was worth it. We all see the same jobs, same stages. No one's chasing each other asking 'did you send the quote yet?'",
      name: "Tom McAllister",
      role: "Plumbing & heating contractor, Edinburgh",
    },
  ],
  US: [
    {
      quote:
        "Used to chase invoices on Sunday nights. Now I spend ten minutes a week on admin. The smart capture is the killer feature -- I describe the job out loud and it fills in the rest.",
      name: "Marcus Hale",
      role: "HVAC contractor, Austin TX",
    },
    {
      quote:
        "Every electrician I know is drowning in texts and spreadsheets. Manos sorted all of that in about an hour. My clients get clean invoices, I get paid faster. Can't ask for more.",
      name: "Priya Patel",
      role: "Electrician, Boston MA",
    },
    {
      quote:
        "I run a four-person plumbing crew and the shared client list alone was worth it. Everyone sees the same jobs, same stages. No more 'did you send the estimate yet?' phone calls.",
      name: "Tom Mahoney",
      role: "Plumbing contractor, Denver CO",
    },
  ],
  CA: [
    {
      quote:
        "I was doing invoices on the kitchen table every Sunday. Now it's ten minutes once a week. Voice capture between jobs is the killer -- I just say what happened and it's logged.",
      name: "Daniel Tremblay",
      role: "HVAC contractor, Montreal QC",
    },
    {
      quote:
        "Half my friends are sparkies and they're all losing track of inspection certs. Manos pings me a month before each one's due. I never miss a renewal anymore.",
      name: "Aisha Chen",
      role: "Electrician, Toronto ON",
    },
    {
      quote:
        "We're a three-truck plumbing outfit and the shared client list alone made it worth it. No more double-booking, no more 'who did Mrs Singh's last service?'.",
      name: "Greg Olsen",
      role: "Plumbing contractor, Vancouver BC",
    },
  ],
  EU: [
    {
      quote:
        "Ich habe am Wochenende nicht mehr mit Excel gekämpft. Manos erfasst den Auftrag vom Telefongespräch direkt und die Rechnung ist fertig, bevor ich zu Hause bin.",
      name: "Katrin Vogel",
      role: "Installateurin, München",
    },
    {
      quote:
        "Avant Manos, je jonglais avec WhatsApp, un carnet et un tableur. Aujourd'hui tout est au même endroit et mes clients reçoivent des factures propres.",
      name: "Julien Moreau",
      role: "Électricien, Lyon",
    },
    {
      quote:
        "Ons hebben een klein schilderteam en sinds Manos weet iedereen dezelfde dingen: zelfde klanten, zelfde planning. Minder bellen, meer werken.",
      name: "Sanne de Vries",
      role: "Schildersbedrijf, Rotterdam",
    },
  ],
  ROW: [
    {
      quote:
        "I run a mobile grooming business across three suburbs. Manos replaced two apps and a notebook. Clients get text invoices, I get paid the same day.",
      name: "Amelia Ross",
      role: "Dog groomer, Sydney",
    },
    {
      quote:
        "My shop went from paper booking cards to a proper client history in about a week. Rebookings doubled because everyone gets a reminder now.",
      name: "Thandiwe Khumalo",
      role: "Hair stylist, Cape Town",
    },
    {
      quote:
        "Used to chase invoices on Sunday nights. Now I spend ten minutes a week on admin. The smart capture is the killer feature -- I describe the job out loud and it fills in the rest.",
      name: "Liam O'Connor",
      role: "Builder, Auckland",
    },
  ],
};

export default function ({ loaderData }: Route.ComponentProps) {
  const { region, country } = loaderData;
  const testimonials = TESTIMONIALS_BY_REGION[region];
  const soloPrice = entryMonthlyDisplay("solo", country);
  const teamPrice = entryMonthlyDisplay("team", country);
  const promoPrice = promoMonthlyDisplay(country);
  const testimonialSubhead =
    region === "GB"
      ? "From sole traders to small teams across the UK."
      : region === "US"
        ? "From sole traders to small crews across the US."
        : region === "CA"
          ? "From sole traders to small crews across Canada."
          : region === "EU"
            ? "From sole traders to small teams across Europe."
            : "From sole traders to small teams around the world.";
  // Schema.org JSON-LD for the homepage. Mirrors the trade-page graph
  // (Organization + SoftwareApplication) but without the per-trade
  // Service or FAQPage. Region-aware areaServed so US/CA/AU visitors
  // don't get described as serviced in the UK only.
  const ldJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://manos.team/#org",
        name: "Manos",
        url: "https://manos.team",
        logo: "https://manos.team/logo-mark.svg",
        sameAs: [],
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://manos.team/#product",
        name: "Manos",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "Jobs, clients, and invoices for sole traders, pairs, and small teams running every kind of service business.",
        brand: { "@id": "https://manos.team/#org" },
        image: "https://manos.team/og-image.png",
        url: "https://manos.team",
        // areaServed accepts an array. We serve one canonical URL
        // across four markets (UK + US + CA + AU) with on-page
        // region detection for currency/copy. The array tells Google
        // we cover all four rather than trying to pick one based on
        // the visitor's geo -- a per-visitor narrowing would have
        // misrepresented our coverage in the SERP.
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "Country", name: "United States" },
          { "@type": "Country", name: "Canada" },
        ],
      },
    ],
  };

  return (
    <div className="overflow-hidden">
      <script
        type="application/ld+json"
        // Stringified once; React serialises this as the script body.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />
      {/* ─── Hero ─── */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — copy */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]">
                  Spend the day on your customers,{" "}
                  <span className="text-amber-600">not your computer</span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                  Jobs, clients, and invoices in one place. Manos handles the
                  admin so you can focus on the work.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="h-12 px-8 text-base bg-amber-600 hover:bg-amber-700 text-white">
                  <Link to="/sign-up" data-attr="home-hero-signup">
                    Get started free
                    <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-8 text-base">
                  <Link to="/sign-in" data-attr="home-hero-signin">Sign in</Link>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {`3 days free, then ${promoPrice} for 3 months. Cancel anytime.`}
              </p>
            </div>

            {/* Right — product visual */}
            <div className="hidden lg:block">
              <div className="relative">
                {/* Mock app screen */}
                <div className="rounded-2xl border bg-card shadow-2xl shadow-amber-500/5 p-6 space-y-4">
                  <div className="flex items-center gap-3 pb-4 border-b">
                    <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Lightning className="h-4 w-4 text-amber-600" weight="fill" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Inbox</p>
                      <p className="text-xs text-muted-foreground">3 items need attention</p>
                    </div>
                  </div>
                  {[
                    { title: "Mrs Patterson — Boiler service", stage: "Quoted", color: "#F59E0B" },
                    { title: "Dave Wilson — Radiator install", stage: "Scheduled", color: "#3B82F6" },
                    { title: "Sarah Chen — Emergency leak", stage: "Enquiry", color: "#6B7280" },
                  ].map((item) => (
                    <div key={item.title} className="flex items-center gap-3 p-3 rounded-lg border">
                      <div className="w-1 h-10 rounded-full" style={{ backgroundColor: item.color }} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.title}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: item.color + "20", color: item.color }}>
                          {item.stage}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Decorative blur */}
                <div className="absolute -z-10 -top-8 -right-8 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stats / Social Proof ─── */}
      <FadeInOnScroll>
      <section className="py-12 border-y bg-stone-50/50 dark:bg-secondary/20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <Stat value="2 sec" label="to capture a job" />
            <Stat value="30 sec" label="to send an invoice" />
            <Stat value="3 days" label="free to try" />
            <Stat value="Any service" label="covered" />
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* Trust badges (Gas Safe / NICEIC / TrustMark / Checkatrade /
          NAPIT / etc.) used to live here but were removed on
          2026-04-26: Manos itself isn't accredited under any of these
          schemes -- they apply to individual tradespeople, not to a
          SaaS company. They'll resurface as an opt-in accreditation
          picker on each trader's public profile (/p/:token), where
          they're genuinely a trust signal for that trader's own
          customers. Don't put them back on the marketing homepage. */}

      {/* ─── Features Grid ─── */}
      <FadeInOnScroll>
      <section className="py-24" id="features">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">
              Built for the work you actually do
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              No learning curve, no feature bloat. Just what you use every day.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Lightning className="h-6 w-6" weight="duotone" />}
              title="Quick capture"
              description="Phone call in the attic? Type a few words — AI extracts client, address, and job details in seconds."
            />
            <FeatureCard
              icon={<UsersThree className="h-6 w-6" weight="duotone" />}
              title="Clients & pipeline"
              description="Every client's details, work history, and invoices in one place. Configurable pipeline stages that match your workflow."
            />
            <FeatureCard
              icon={<Invoice className="h-6 w-6" weight="duotone" />}
              title="Invoicing"
              description="Create invoices from jobs in one tap. Line items, VAT, send to client. Professional-looking, zero effort."
            />
            <FeatureCard
              icon={<CalendarBlank className="h-6 w-6" weight="duotone" />}
              title="Schedule"
              description="Weekly calendar with addresses. Who you're seeing, when, and where. Not project management — just clarity."
            />
            <FeatureCard
              icon={<Camera className="h-6 w-6" weight="duotone" />}
              title="Photos & evidence"
              description="Snap photos as you work — before shots, completed work, receipts — attached straight to the client file. Proof of work and end of paperwork panic."
            />
            <FeatureCard
              icon={<ShieldCheck className="h-6 w-6" weight="duotone" />}
              title="Accounting made easy"
              description={taxCopyForCountry(country)}
            />
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── How It Works ─── */}
      <FadeInOnScroll>
      <section className="py-24 bg-stone-50/50 dark:bg-secondary/20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-16 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">
              Built for how you actually work
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Not another enterprise tool dumbed down. Built for sole traders,
              pairs, and small teams -- running every kind of service business.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <Step number={1} title="Capture" description="Get a phone call, a text, a WhatsApp message — type or dictate what needs doing. Manos figures out the rest." />
            <Step number={2} title="Work" description="Your pipeline shows every job from enquiry to paid. Advance stages with a tap. Photos, notes, quotes — all attached." />
            <Step number={3} title="Get paid" description="Invoice generated automatically. Send via email or text. Client pays by card, bank transfer, or cash. You track it all." />
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── Who It's For ─── */}
      <FadeInOnScroll>
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-6 text-center space-y-8">
          <h2 className="text-3xl font-bold tracking-tight">
            Works for every kind of service business
          </h2>
          <p className="text-sm text-muted-foreground">
            Manos -- Spanish for hands. Built for the people who do the work themselves.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {/* Chips render one link per per-trade landing page (see
                web/lib/for-trades.ts). Sourcing from TRADES guarantees
                every chip lands on a real page and new trades appear
                automatically when added to the content file. */}
            {TRADES.map((trade) => (
              <Link
                key={trade.slug}
                to={`/for/${trade.slug}`}
                className="px-4 py-2 rounded-full bg-stone-100 dark:bg-stone-800 text-sm font-medium hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
              >
                {trade.plural}
              </Link>
            ))}
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── Testimonials ─── */}
      <FadeInOnScroll>
      <section className="py-24 bg-stone-50/50 dark:bg-secondary/20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">
              What people are saying
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {testimonialSubhead}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <TestimonialCard
                key={t.name}
                quote={t.quote}
                name={t.name}
                role={t.role}
                stars={5}
              />
            ))}
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── Mobile apps coming soon ─── */}
      <FadeInOnScroll>
      <section className="py-20 lg:py-24 bg-gradient-to-br from-amber-600 to-amber-700 dark:from-amber-700 dark:to-amber-800 text-white relative overflow-hidden">
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-[3fr_2fr] gap-12 lg:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                <span className="h-2 w-2 rounded-full bg-amber-200 animate-pulse" />
                Coming soon
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Native iOS and Android apps,
                <br />
                landing in a few weeks.
              </h2>
              <p className="text-lg text-amber-50 leading-relaxed max-w-lg">
                The web app already works beautifully on a phone. The native
                apps are with the App Store and Play Store going through their
                review process. Once they're through, you'll get the full
                Manos experience straight from your home screen --
                push notifications, fingerprint sign-in, voice capture from
                anywhere, and offline mode for when you're working in a basement.
              </p>
              <p className="text-sm text-amber-100 pt-2">
                In review with Apple and Google now.
              </p>
              <p className="text-sm text-amber-100/90 pt-2">
                Sign up today and the apps will be ready and waiting for you on
                day one. No migration. No data move. Same account.
              </p>
            </div>

            {/* Right -- phone-frame mock with the Manos amber branding */}
            <div className="hidden lg:block">
              <div className="relative mx-auto w-[260px]">
                <div className="rounded-[2.5rem] bg-stone-900 p-3 shadow-2xl shadow-black/40 border border-stone-700">
                  <div className="rounded-[2rem] bg-gradient-to-b from-amber-50 to-amber-100 dark:from-amber-100 dark:to-amber-200 aspect-[9/19] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-stone-900 rounded-full" />
                    {/* App content mock */}
                    <div className="pt-12 px-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-amber-900">Manos</span>
                        <Camera className="h-4 w-4 text-amber-800" weight="duotone" />
                      </div>
                      <div className="rounded-xl bg-white p-3 shadow-sm space-y-2">
                        <p className="text-[11px] font-bold text-stone-900">Today</p>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          <span className="text-stone-700">Mrs Jones, dishwasher</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          <span className="text-stone-700">22 Acacia, leak survey</span>
                        </div>
                      </div>
                      <div className="rounded-xl bg-amber-600 text-white p-3 text-center">
                        <Camera className="h-5 w-5 mx-auto mb-1" weight="fill" />
                        <p className="text-[10px] font-bold uppercase tracking-wider">
                          Quick capture
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── Pricing ─── */}
      <FadeInOnScroll>
      <section className="py-24 bg-stone-50/50 dark:bg-secondary/20" id="pricing">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">Pricing that grows with you</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Pick a seat size, get every feature. Card commission tiers down as you grow. No seat limits on Enterprise.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Solo */}
            <div className="rounded-2xl border-2 border-amber-400 dark:border-amber-600 p-6 bg-white dark:bg-card flex flex-col relative">
              <div className="absolute -top-3 left-4">
                <span className="text-xs font-bold uppercase tracking-wider bg-amber-600 text-white px-3 py-1 rounded-full">
                  Most popular
                </span>
              </div>
              <p className="text-xs uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-400 mb-2 mt-1">{PLANS.solo.displayName}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-extrabold">{soloPrice}</span>
              </div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                Launch offer: {promoPrice} for 3 months
              </p>
              <p className="text-xs text-muted-foreground mb-6">1 user -- sole traders &amp; freelancers</p>
              <ul className="space-y-2.5 text-sm text-muted-foreground mb-8 flex-1">
                {PLANS.solo.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" weight="fill" />
                    {feature}
                  </li>
                ))}
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" weight="fill" /> 1.0% card application fee</li>
              </ul>
              <Button asChild className="w-full h-10 text-sm bg-amber-600 hover:bg-amber-700 text-white">
                <Link to="/sign-up" data-attr="home-pricing-solo-signup">Start free trial</Link>
              </Button>
            </div>
            {/* Team */}
            <div className="rounded-2xl border p-6 bg-white dark:bg-card flex flex-col">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">{PLANS.team.displayName}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-extrabold">{teamPrice}</span>
              </div>
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
                Launch offer: {promoPrice} for 3 months
              </p>
              <p className="text-xs text-muted-foreground mb-6">Up to 3 users -- small firms &amp; trainees</p>
              <ul className="space-y-2.5 text-sm text-muted-foreground mb-8 flex-1">
                {PLANS.team.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" weight="fill" />
                    {feature}
                  </li>
                ))}
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" weight="fill" /> 0.7% card application fee</li>
              </ul>
              <Button asChild variant="outline" className="w-full h-10 text-sm">
                <Link to="/sign-up" data-attr="home-pricing-team-signup">Start free trial</Link>
              </Button>
            </div>
            {/* Enterprise */}
            <div className="rounded-2xl border p-6 bg-stone-900 dark:bg-stone-950 flex flex-col">
              <p className="text-xs uppercase tracking-wide font-semibold text-stone-400 mb-2">{PLANS.enterprise.displayName}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-extrabold text-white">Custom</span>
              </div>
              <p className="text-xs text-stone-400 mb-6">FM portfolios &amp; white-label deals</p>
              <ul className="space-y-2.5 text-sm text-stone-300 mb-8 flex-1">
                {PLANS.enterprise.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-stone-400 shrink-0 mt-0.5" weight="fill" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button asChild className="w-full h-10 text-sm bg-amber-600 hover:bg-amber-500 text-white font-semibold">
                <Link to="/contact?intent=enterprise" data-attr="home-pricing-enterprise-contact">Talk to us</Link>
              </Button>
            </div>
          </div>
          <p className="text-xs text-center text-muted-foreground mt-8">
            Launch offer: 3 days free, then {promoPrice} for 3 months on Solo or Team.
            Card application fee tiers down as you grow. Cash and bank transfer: always zero commission.
          </p>
        </div>
      </section></FadeInOnScroll>

      {/* ─── FAQ ─── */}
      <FadeInOnScroll>
      <section className="py-24 bg-stone-50/50 dark:bg-secondary/20" id="faq">
        <div className="mx-auto max-w-3xl px-6">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">
              Common questions
            </h2>
          </div>
          <div className="space-y-2">
            <FAQItem
              question="How does the free trial work?"
              answer={`Pick Solo or Team at signup -- you'll get 3 days completely free, no card charged. After that, our launch offer kicks in: ${promoPrice} for 3 months, then the full plan price (${soloPrice} Solo, ${teamPrice} Team) from month 4. Cancel anytime from the billing portal.`}
            />
            <FAQItem
              question="What happens to my data if I cancel?"
              answer="You keep your data. Export everything as CSV before you go. If you simply cancel, we hold on to your records for 90 days in case you change your mind. If you'd rather we deleted everything sooner, just ask -- we'll wipe it within 30 days. More on this on our About page."
            />
            <FAQItem
              question="Does Manos handle tax record-keeping?"
              answer={`Yes. Manos keeps digital records of all income and expenses. ${taxCopyForCountry(country)}`}
            />
            <FAQItem
              question="Can I use Manos on my phone?"
              answer="Yes -- the web app works perfectly on a phone browser today, and most of the features (quick capture, voice notes, schedule) are designed for one-handed use on the move. Native iOS and Android apps are with the App Store and Play Store now and should be available within a few weeks."
            />
            <FAQItem
              question="How does card payment commission work?"
              answer="Solo: 1.0%, Team: 0.7%, Enterprise: negotiated. This is our application fee on top of Stripe's base processing rate. Cash and bank transfer are always zero commission -- the seat covers the platform. Upgrading to Team is break-even at roughly 6,667/mo of card volume."
            />
            <FAQItem
              question="Who is Manos for?"
              answer="Any service business. It's most used by plumbers, electricians, gas engineers, builders, landscapers, and cleaners -- but it works just as well for hair stylists, personal trainers, tutors, and anyone else running a one-person or small-team service business."
            />
          </div>
        </div>
      </section></FadeInOnScroll>

      {/* ─── Final CTA ─── */}
      <FadeInOnScroll>
      <section className="py-20 bg-amber-600 dark:bg-amber-700">
        <div className="mx-auto max-w-2xl px-6 text-center space-y-6">
          <h2 className="text-3xl font-bold text-white">
            Ready to spend less time on admin?
          </h2>
          <p className="text-amber-100">
            {`3 days free, then ${promoPrice} for 3 months on Solo or Team. No contract, cancel anytime.`}
          </p>
          <Button asChild size="lg" className="h-12 px-10 text-base bg-white text-amber-700 hover:bg-amber-50 font-bold">
            <Link to="/sign-up" data-attr="home-bottom-cta-signup">
              Get started free
              <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
            </Link>
          </Button>
        </div>
      </section></FadeInOnScroll>
    </div>
  );
}

// ─── Components ───

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-2xl md:text-3xl font-extrabold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 rounded-2xl border bg-card hover:shadow-md transition-shadow space-y-3">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center text-amber-600 shrink-0">
          {icon}
        </div>
        <h3 className="font-semibold text-lg">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function Step({
  number,
  title,
  description,
}: {
  number: number;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center space-y-4">
      <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 flex items-center justify-center text-lg font-extrabold mx-auto">
        {number}
      </div>
      <h3 className="text-lg font-bold">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function TestimonialCard({
  quote,
  name,
  role,
  stars,
}: {
  quote: string;
  name: string;
  role: string;
  stars: number;
}) {
  return (
    <div className="p-6 rounded-2xl border bg-white dark:bg-card space-y-4">
      <div className="flex gap-0.5">
        {Array.from({ length: stars }).map((_, i) => (
          <Star key={i} className="h-4 w-4 text-amber-500" weight="fill" />
        ))}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">"{quote}"</p>
      <div>
        <p className="text-sm font-semibold">{name}</p>
        <p className="text-xs text-muted-foreground">{role}</p>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white dark:bg-card overflow-hidden">
      <button
        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-stone-50 dark:hover:bg-secondary/40 transition-colors"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold">{question}</span>
        <CaretDown
          className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          weight="bold"
        />
      </button>
      {open && (
        <div className="px-6 pb-5">
          <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}
